import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import useSWR, { useSWRConfig } from "swr";
import api from "@/lib/api";
import { listAddressQueue, listScanQueue } from "@/lib/offlineDb";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MapPin, Plus, Search, Navigation, CheckCircle2, AlertTriangle, ScanLine, CloudOff, RefreshCw } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const AddressFinderHome = () => {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [pos, setPos] = useState(null);
  const [pending, setPending] = useState({ addresses: 0, scans: 0 });
  const { mutate } = useSWRConfig();
  const { data: list } = useSWR(`/address-finder${q ? `?q=${encodeURIComponent(q)}` : ""}`, fetcher);
  const { data: near } = useSWR(pos ? `/address-finder/nearby?lat=${pos.lat}&lng=${pos.lng}&radius_m=5000` : null, fetcher);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => {}, { timeout: 8000 }
    );
  }, []);

  // Watch the local offline queue so drivers see "1 pending" instantly after saving offline
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      const [a, s] = await Promise.all([listAddressQueue(), listScanQueue()]);
      if (alive) setPending({ addresses: a.length, scans: s.length });
    };
    refresh();
    const onSynced = () => {
      refresh();
      // Force a fresh reload of the list — belt & braces with global SWR mutate.
      mutate((key) => typeof key === "string" && key.startsWith("/address-finder"));
    };
    window.addEventListener("myportal:synced", onSynced);
    window.addEventListener("online", refresh);
    const t = setInterval(refresh, 8000);
    return () => {
      alive = false;
      window.removeEventListener("myportal:synced", onSynced);
      window.removeEventListener("online", refresh);
      clearInterval(t);
    };
  }, [mutate]);

  const groups = {};
  (list?.addresses || []).forEach((a) => {
    const k = a.postcode_area || "OTHER";
    (groups[k] = groups[k] || []).push(a);
  });

  return (
    <div className="p-4 pt-6 space-y-4 pb-24">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Address Finder</div>
        <h1 className="font-heading text-2xl font-bold tracking-tight mt-1">Difficult addresses</h1>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search postcode, farm, business…"
               className="pl-9" data-testid="af-search" />
      </div>

      <Button onClick={() => nav("/driver/address-finder/quick-find")} data-testid="af-quick-find-btn"
              className="w-full h-14 bg-brand-red hover:bg-brand-red-hover text-base">
        <ScanLine className="w-5 h-5 mr-2" /> Scan a label / Quick Find
      </Button>

      <Button onClick={() => nav("/driver/address-finder/add")} data-testid="af-add-btn"
              variant="outline" className="w-full h-12">
        <Plus className="w-5 h-5 mr-2" /> Add new address manually
      </Button>

      {(pending.addresses > 0 || pending.scans > 0) && (
        <Card className="p-3 rounded-2xl border-amber-200 bg-amber-50 flex items-center gap-3" data-testid="af-pending">
          {navigator.onLine ? (
            <RefreshCw className="w-4 h-4 text-amber-600 animate-spin" />
          ) : (
            <CloudOff className="w-4 h-4 text-amber-600" />
          )}
          <div className="flex-1 text-xs text-amber-800">
            <div className="font-semibold">
              {pending.addresses + pending.scans} saved on this device
            </div>
            <div className="text-amber-700">
              {navigator.onLine
                ? "Syncing to the server now — they'll appear in the list any moment."
                : "You're offline. They'll sync automatically when you get signal."}
            </div>
          </div>
        </Card>
      )}

      {near?.addresses?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">Nearby</div>
          <div className="space-y-2">
            {near.addresses.slice(0, 5).map((a) => <AddressCard key={a.id} a={a} nav={nav} />)}
          </div>
        </div>
      )}

      {Object.keys(groups).sort().map((area) => (
        <div key={area}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">{area}</div>
          <div className="space-y-2">
            {groups[area].map((a) => <AddressCard key={a.id} a={a} nav={nav} />)}
          </div>
        </div>
      ))}

      {list && list.count === 0 && (
        <Card className="p-8 text-center text-sm text-slate-500 rounded-2xl">
          No addresses yet. Tap “Add new address”.
        </Card>
      )}
    </div>
  );
};

const AddressCard = ({ a, nav }) => (
  <Card className="p-3 rounded-2xl border-brand-border" onClick={() => nav(`/driver/address-finder/${a.id}`)}
        data-testid={`af-card-${a.id}`}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-brand-red/10 grid place-items-center">
        <MapPin className="w-5 h-5 text-brand-red" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-sm truncate">{a.title}</div>
        <div className="text-xs text-slate-500 truncate">
          {a.postcode}{a.town ? ` · ${a.town}` : ""}
          {a.distance_m != null ? ` · ${a.distance_m}m` : ""}
        </div>
      </div>
      {a.status === "verified" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
      {a.vehicle_size_warning && <AlertTriangle className="w-4 h-4 text-amber-600" />}
      <Navigation className="w-4 h-4 text-brand-navy" />
    </div>
  </Card>
);

export default AddressFinderHome;
